import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { testAPI } from '../services/api';
import {
  TrophyIcon,
  AcademicCapIcon,
  ArrowLeftIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const TopPerformers = ({ currentStudentId = null, testAPI: customTestAPI = null }) => {
  // Use custom API if provided, otherwise use default
  const apiService = customTestAPI || testAPI;
  // Add sparkle animation
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes twinkle {
        0%, 100% { opacity: 0; transform: scale(0); }
        50% { opacity: 1; transform: scale(1); }
      }
      @keyframes pulse-glow {
        0%, 100% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.5); }
        50% { box-shadow: 0 0 30px rgba(59, 130, 246, 0.8); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  const navigate = useNavigate();
  const [performers, setPerformers] = useState([]);
  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTests();
    loadPerformers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiService]);

  const loadTests = async () => {
    try {
      const response = await apiService.getAll(0, 1000, true);
      setTests(response.data.tests || []);
    } catch (error) {
      console.error('Error loading tests:', error);
    }
  };

  const loadPerformers = async (testId = null) => {
    setLoading(true);
    try {
      const response = await apiService.getTopPerformers(testId);
      setPerformers(response.data || []);
    } catch (error) {
      console.error('Error loading top performers:', error);
      setPerformers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTestFilter = (testId) => {
    setSelectedTest(testId);
    loadPerformers(testId || null);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getPodiumHeight = (rank) => {
    switch (rank) {
      case 1: return 'h-56';
      case 2: return 'h-44';
      case 3: return 'h-36';
      default: return 'h-32';
    }
  };

  const getPodiumColor = (rank) => {
    switch (rank) {
      case 1: return 'from-yellow-400 via-yellow-500 to-yellow-600';
      case 2: return 'from-gray-300 via-gray-400 to-gray-500';
      case 3: return 'from-orange-400 via-orange-500 to-orange-600';
      default: return 'from-gray-600 to-gray-700';
    }
  };

  const getMedalIcon = (rank) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return rank;
    }
  };

  const getRankBorderColor = (rank) => {
    switch (rank) {
      case 1: return 'border-yellow-400 shadow-yellow-400/50';
      case 2: return 'border-gray-300 shadow-gray-300/50';
      case 3: return 'border-orange-400 shadow-orange-400/50';
      default: return 'border-gray-600';
    }
  };

  const topThree = performers.slice(0, 3);
  const restOfList = performers.slice(3);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            <span>Back</span>
          </button>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-yellow-400 to-yellow-600 p-3 rounded-xl">
                <TrophyIcon className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Top Performers</h1>
                <p className="text-gray-400 mt-1">Student leaderboard by average test scores</p>
              </div>
            </div>

            {/* Test Filter */}
            <div className="flex items-center gap-2">
              <AcademicCapIcon className="h-5 w-5 text-gray-400" />
              <select
                value={selectedTest}
                onChange={(e) => handleTestFilter(e.target.value)}
                className="bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Tests</option>
                {tests.map(test => (
                  <option key={test.test_id} value={test.test_id}>
                    {test.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {performers.length === 0 ? (
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-12 text-center">
            <ChartBarIcon className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">No test data available</p>
            <p className="text-gray-500 text-sm mt-2">Students need to complete tests to appear here</p>
          </div>
        ) : (
          <>
            {/* Podium - Top 3 */}
            {topThree.length > 0 && (
              <div className="mb-12">
                <div className="bg-gray-800/30 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-8">
                  <div className="flex items-end justify-center gap-6 max-w-4xl mx-auto">
                    {/* 2nd Place */}
                    {topThree[1] && (
                      <div className={`flex flex-col items-center flex-1 max-w-xs ${currentStudentId === topThree[1].student_id ? 'relative' : ''}`}>
                        {currentStudentId === topThree[1].student_id && (
                          <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full z-20 whitespace-nowrap">
                            You
                          </div>
                        )}
                        {/* Student Info */}
                        <div className="mb-4 flex flex-col items-center">
                          <div className="relative mb-3">
                            {topThree[1].profile_picture_base64 ? (
                              <img
                                src={`data:image/jpeg;base64,${topThree[1].profile_picture_base64}`}
                                alt={topThree[1].student_name}
                                className={`w-20 h-20 sm:w-24 sm:h-24 rounded-full border-4 ${currentStudentId === topThree[1].student_id ? 'border-blue-500 shadow-blue-500/50' : getRankBorderColor(2)} shadow-lg object-cover ${currentStudentId === topThree[1].student_id ? 'ring-4 ring-blue-400/30' : ''}`}
                              />
                            ) : (
                              <div className={`w-20 h-20 sm:w-24 sm:h-24 rounded-full border-4 ${currentStudentId === topThree[1].student_id ? 'border-blue-500 shadow-blue-500/50' : getRankBorderColor(2)} bg-gray-500/20 shadow-lg flex items-center justify-center ${currentStudentId === topThree[1].student_id ? 'ring-4 ring-blue-400/30' : ''}`}>
                                <span className="text-2xl font-bold text-gray-300">
                                  {getInitials(topThree[1].student_name)}
                                </span>
                              </div>
                            )}
                            <div className="absolute -top-3 -right-3 text-4xl drop-shadow-lg">
                              {getMedalIcon(2)}
                            </div>
                          </div>
                          <p className="text-sm sm:text-base font-bold text-white text-center line-clamp-1 px-2">
                            {topThree[1].student_name}
                          </p>
                          <p className="text-2xl sm:text-3xl font-bold text-gray-300 mt-2 drop-shadow-lg">
                            {topThree[1].average_score}%
                          </p>
                          <p className="text-xs text-gray-400 mt-1">{topThree[1].tests_taken} test{topThree[1].tests_taken !== 1 ? 's' : ''}</p>
                        </div>
                        {/* Podium Block */}
                        <div className={`w-full ${getPodiumHeight(2)} bg-gradient-to-br ${getPodiumColor(2)} rounded-t-2xl shadow-xl flex items-center justify-center text-white font-bold text-5xl relative overflow-hidden`}>
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                          <div className="absolute inset-0 bg-white/10"></div>
                          <span className="relative z-10 drop-shadow-lg">2</span>
                        </div>
                      </div>
                    )}

                    {/* 1st Place - Center and Tallest */}
                    {topThree[0] && (
                      <div className={`flex flex-col items-center flex-1 max-w-xs ${currentStudentId === topThree[0].student_id ? 'relative' : ''}`}>
                        {currentStudentId === topThree[0].student_id && (
                          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full z-20 whitespace-nowrap">
                            You
                          </div>
                        )}
                        {/* Crown Icon */}
                        <div className="text-5xl mb-2 animate-bounce">👑</div>
                        {/* Student Info */}
                        <div className="mb-4 flex flex-col items-center">
                          <div className="relative mb-3">
                            {topThree[0].profile_picture_base64 ? (
                              <img
                                src={`data:image/jpeg;base64,${topThree[0].profile_picture_base64}`}
                                alt={topThree[0].student_name}
                                className={`w-24 h-24 sm:w-28 sm:h-28 rounded-full border-4 ${currentStudentId === topThree[0].student_id ? 'border-blue-500 shadow-blue-500/50' : getRankBorderColor(1)} shadow-2xl object-cover ${currentStudentId === topThree[0].student_id ? 'ring-4 ring-blue-400/30' : ''}`}
                              />
                            ) : (
                              <div className={`w-24 h-24 sm:w-28 sm:h-28 rounded-full border-4 ${currentStudentId === topThree[0].student_id ? 'border-blue-500 shadow-blue-500/50' : getRankBorderColor(1)} bg-yellow-500/20 shadow-2xl flex items-center justify-center ${currentStudentId === topThree[0].student_id ? 'ring-4 ring-blue-400/30' : ''}`}>
                                <span className="text-3xl font-bold text-yellow-400">
                                  {getInitials(topThree[0].student_name)}
                                </span>
                              </div>
                            )}
                            <div className="absolute -top-3 -right-3 text-5xl drop-shadow-lg">
                              {getMedalIcon(1)}
                            </div>
                          </div>
                          <p className="text-base sm:text-lg font-bold text-white text-center line-clamp-1 px-2">
                            {topThree[0].student_name}
                          </p>
                          <p className="text-3xl sm:text-4xl font-bold text-yellow-400 mt-2 drop-shadow-lg">
                            {topThree[0].average_score}%
                          </p>
                          <p className="text-xs text-gray-400 mt-1">{topThree[0].tests_taken} test{topThree[0].tests_taken !== 1 ? 's' : ''}</p>
                        </div>
                        {/* Podium Block */}
                        <div className={`w-full ${getPodiumHeight(1)} bg-gradient-to-br ${getPodiumColor(1)} rounded-t-2xl shadow-2xl flex items-center justify-center text-white font-bold text-6xl relative overflow-hidden`}>
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                          <div className="absolute inset-0 bg-white/10"></div>
                          <div className="absolute inset-0">
                            {[...Array(5)].map((_, i) => (
                              <div
                                key={i}
                                className="absolute w-2 h-2 bg-white/30 rounded-full"
                                style={{
                                  top: `${Math.random() * 100}%`,
                                  left: `${Math.random() * 100}%`,
                                  animation: `twinkle ${2 + Math.random() * 2}s infinite ${Math.random() * 2}s`,
                                }}
                              />
                            ))}
                          </div>
                          <span className="relative z-10 drop-shadow-lg">1</span>
                        </div>
                      </div>
                    )}

                    {/* 3rd Place */}
                    {topThree[2] && (
                      <div className={`flex flex-col items-center flex-1 max-w-xs ${currentStudentId === topThree[2].student_id ? 'relative' : ''}`}>
                        {currentStudentId === topThree[2].student_id && (
                          <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full z-20 whitespace-nowrap">
                            You
                          </div>
                        )}
                        {/* Student Info */}
                        <div className="mb-4 flex flex-col items-center">
                          <div className="relative mb-3">
                            {topThree[2].profile_picture_base64 ? (
                              <img
                                src={`data:image/jpeg;base64,${topThree[2].profile_picture_base64}`}
                                alt={topThree[2].student_name}
                                className={`w-18 h-18 sm:w-20 sm:h-20 rounded-full border-4 ${currentStudentId === topThree[2].student_id ? 'border-blue-500 shadow-blue-500/50' : getRankBorderColor(3)} shadow-lg object-cover ${currentStudentId === topThree[2].student_id ? 'ring-4 ring-blue-400/30' : ''}`}
                              />
                            ) : (
                              <div className={`w-18 h-18 sm:w-20 sm:h-20 rounded-full border-4 ${currentStudentId === topThree[2].student_id ? 'border-blue-500 shadow-blue-500/50' : getRankBorderColor(3)} bg-orange-500/20 shadow-lg flex items-center justify-center ${currentStudentId === topThree[2].student_id ? 'ring-4 ring-blue-400/30' : ''}`}>
                                <span className="text-xl font-bold text-orange-400">
                                  {getInitials(topThree[2].student_name)}
                                </span>
                              </div>
                            )}
                            <div className="absolute -top-3 -right-3 text-4xl drop-shadow-lg">
                              {getMedalIcon(3)}
                            </div>
                          </div>
                          <p className="text-sm sm:text-base font-bold text-white text-center line-clamp-1 px-2">
                            {topThree[2].student_name}
                          </p>
                          <p className="text-xl sm:text-2xl font-bold text-orange-400 mt-2 drop-shadow-lg">
                            {topThree[2].average_score}%
                          </p>
                          <p className="text-xs text-gray-400 mt-1">{topThree[2].tests_taken} test{topThree[2].tests_taken !== 1 ? 's' : ''}</p>
                        </div>
                        {/* Podium Block */}
                        <div className={`w-full ${getPodiumHeight(3)} bg-gradient-to-br ${getPodiumColor(3)} rounded-t-2xl shadow-xl flex items-center justify-center text-white font-bold text-4xl relative overflow-hidden`}>
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                          <div className="absolute inset-0 bg-white/10"></div>
                          <span className="relative z-10 drop-shadow-lg">3</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Rest of the List - 4th onwards */}
            {restOfList.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <ChartBarIcon className="h-6 w-6 text-blue-400" />
                  Rankings
                </h2>
                <div className="space-y-3">
                  {restOfList.map((performer) => (
                    <div
                      key={performer.student_id}
                      className={`backdrop-blur-sm rounded-xl border p-4 transition-all hover:scale-[1.01] ${
                        currentStudentId === performer.student_id
                          ? 'bg-blue-600/20 border-blue-500 shadow-lg shadow-blue-500/30 animate-pulse-glow'
                          : 'bg-gray-800/50 border-gray-700/50 hover:bg-gray-800'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        {/* Rank */}
                        <div className={`flex-shrink-0 w-12 h-12 bg-gradient-to-br rounded-lg flex items-center justify-center ${
                          currentStudentId === performer.student_id
                            ? 'from-blue-600 to-blue-700'
                            : 'from-gray-700 to-gray-800'
                        }`}>
                          <span className="text-xl font-bold text-white">{performer.rank}</span>
                        </div>

                        {/* Profile Picture */}
                        <div className="flex-shrink-0">
                          {performer.profile_picture_base64 ? (
                            <img
                              src={`data:image/jpeg;base64,${performer.profile_picture_base64}`}
                              alt={performer.student_name}
                              className={`w-12 h-12 rounded-full border-2 object-cover ${
                                currentStudentId === performer.student_id
                                  ? 'border-blue-500 ring-2 ring-blue-400/30'
                                  : 'border-gray-700'
                              }`}
                            />
                          ) : (
                            <div className={`w-12 h-12 rounded-full border-2 bg-gray-700/50 flex items-center justify-center ${
                              currentStudentId === performer.student_id
                                ? 'border-blue-500 ring-2 ring-blue-400/30'
                                : 'border-gray-700'
                            }`}>
                              <span className="text-sm font-semibold text-gray-300">
                                {getInitials(performer.student_name)}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Student Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="text-white font-semibold truncate">{performer.student_name}</h3>
                            {currentStudentId === performer.student_id && (
                              <span className="bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
                                You
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-400">{performer.tests_taken} test{performer.tests_taken !== 1 ? 's' : ''} completed</p>
                        </div>

                        {/* Score */}
                        <div className="flex-shrink-0 text-right">
                          <div className={`text-2xl font-bold ${
                            currentStudentId === performer.student_id ? 'text-blue-400' : 'text-blue-400'
                          }`}>{performer.average_score}%</div>
                          <div className="text-xs text-gray-500">Avg Score</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TopPerformers;
